import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { toAbsoluteUrl } from "../modules/utils/AssetHelpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { BillExampleModal } from "../modules/components/home/BillExampleModal";
import { login } from "../modules/api/auth";
import Swal from "sweetalert2";
import { refreshInvoice } from "../modules/api/RecordInvoiceAPI";

const infoIcon = <FontAwesomeIcon icon={faCircleInfo} className="me-2" />;

const HomeForm = () => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [visible, setVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ฟังก์ชัน format หมายเลขบัตรประชาชนเป็น xx-xxxx-xxxxx-xx-x
  const formatIdCard = (value: string) => {
    // เอาเฉพาะตัวเลข
    const numbers = value.replace(/\D/g, '');

    // จำกัดไม่เกิน 13 หลัก
    const truncated = numbers.slice(0, 13);

    // Format ตามรูปแบบ x-xxxx-xxxxx-xx-x
    let formatted = truncated;
    if (truncated.length > 1) {
      formatted = truncated.slice(0, 1);
      if (truncated.length > 1) {
        formatted += '-' + truncated.slice(1, 5);
      }
      if (truncated.length > 5) {
        formatted += '-' + truncated.slice(5, 10);
      }
      if (truncated.length > 10) {
        formatted += '-' + truncated.slice(10, 12);
      }
      if (truncated.length > 12) {
        formatted += '-' + truncated.slice(12, 13);
      }
    }

    return formatted;
  };

  const handleIdCardChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatIdCard(e.target.value);
    setValue('id_card', formatted);
  };

  const onSubmit = async (data: any) => {
    setIsLoading(true);
    try {
      // ลบ dash ออกก่อนส่ง API
      const idCardNumber = data.id_card.replace(/\D/g, '');

      const response = await login({
        id_card: idCardNumber,
        phone: data.phone,
        device_name: 'web-client'
      });

      // ตรวจสอบ responseStatus จาก API
      if (response?.responseStatus === "false" || response?.responseStatus === false) {
        // กรณีไม่พบข้อมูลผู้ใช้น้ำ
        setIsLoading(false);
        await Swal.fire({
          icon: 'warning',
          title: 'ไม่พบข้อมูลผู้ใช้น้ำ',
          text: response?.responseMessage || 'กรุณาตรวจสอบเลขบัตรประชาชนและเลขมาตร',
          confirmButtonText: 'ปิด',
          confirmButtonColor: '#547DE5',
        });
        return;
      }

      // เก็บ token และ customer.id หรือข้อมูลการ login (ถ้ามี)
      if (response?.data?.accessToken) {
        localStorage.setItem('token', response.data.accessToken);
      }
      if (response?.data?.customer?.id) {
        localStorage.setItem('customerId', response.data.customer.id.toString());
      }

      // แสดง modal สำเร็จ
      await Swal.fire({
        icon: 'success',
        title: 'เข้าสู่ระบบสำเร็จ',
        text: 'กำลังนำท่านไปยังหน้าข้อมูลผู้ใช้น้ำ',
        showConfirmButton: false,
        timer: 1500
      });

      // นำทางไปหน้าถัดไป
      navigate('/record-invoice');
    } catch (error: any) {
      console.error('Login failed:', error);
      setIsLoading(false);

      // จัดการ error จาก API
      const errorMessage = error?.response?.data?.responseMessage
        || error?.response?.data?.message
        || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';

      await Swal.fire({
        icon: 'error',
        title: 'เข้าสู่ระบบไม่สำเร็จ',
        text: errorMessage,
        confirmButtonText: 'ลองอีกครั้ง',
        confirmButtonColor: '#547DE5',
      });
    }
  };






  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      <div className="bg-white text-6F6B7D" style={{ minHeight: "100vh" }}>
        <div className="row g-0" style={{ minHeight: "100vh" }}>
          {/* Left side - Image */}
          {!isMobile && (
            <div
              className="col-md-6"
              style={{
                backgroundImage: `url('${toAbsoluteUrl("/images/bg.svg")}')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
            </div>
          )}

          {/* Right side - Form */}
          <div className={`${isMobile ? "col-12" : "col-md-6"} d-flex justify-content-center align-items-center`}>
            <div className={`${isMobile ? "p-4" : "p-5"}`} style={{ maxWidth: "500px", width: "100%" }}>
              {/* Logo and Title */}
              <div className="d-flex justify-content-center align-items-center mb-5">
                <img
                  src={toAbsoluteUrl("/images/logo.svg")}
                  className="img-fluid rounded-start me-3"
                  alt="logo"
                  style={{
                    width: "500px",
                    height: "auto",
                    objectFit: "cover"
                  }}
                />
              </div>
              {/* Form */}
              <h2 className="mb-2 fs-4">เข้าสู่ระบบ</h2>
              <p className="opacity-50">ตรวจสอบข้อมูลการใช้น้ำ และประวัติการชำระเงินได้ในที่เดียว</p>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-3">
                  <label htmlFor="id_card" className="form-label">
                    หมายเลขบัตรประจำตัวประชาชน
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="id_card"
                    maxLength={17}
                    {...register("id_card", {
                      required: "กรุณากรอกหมายเลขบัตรประชาชน",
                      pattern: {
                        value: /^\d{1}-\d{4}-\d{5}-\d{2}-\d{1}$/,
                        message: "กรุณากรอกหมายเลขบัตรประจำตัวประชาชนให้ครบ 13 หลัก"
                      },
                      onChange: handleIdCardChange
                    })}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="id_card"
                    render={({ message }) => (
                      <p className="text-danger mt-1 mb-0">{message}</p>
                    )}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="phone" className="form-label">
                    เบอร์โทรศัพท์
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="phone"
                    {...register("phone", {
                      required: "กรุณากรอกหมายเลขเขต",
                    })}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="phone"
                    render={({ message }) => (
                      <p className="text-danger mt-1 mb-0">{message}</p>
                    )}
                  />
                </div>

                {/* <div className="mb-3">
                  <label htmlFor="phone" className="form-label">
                    รหัสมาตรวัดน้ำ
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="meter_number"
                    {...register("meter_number", {
                      required: "กรุณากรอกรหัสมาตรวัดน้ำ",
                    })}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="meter_number"
                    render={({ message }) => (
                      <p className="text-danger mt-1 mb-0">{message}</p>
                    )}
                  />
                </div> */}

                <div className="mb-3">
                  <button
                    type="submit"
                    className="btn w-100 fs-5 fw-normal d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: isLoading ? "#2d62c6" : "#547DE5",
                      color: "#fff",
                      minHeight: "48px"
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="d-flex align-items-center justify-content-center">
                        <FontAwesomeIcon icon={faSpinner} className="me-2" spin={true} />
                        <span style={{ fontFamily: 'google sans, sans-serif', color: '#fff' }}>กำลังเข้าสู่ระบบ...</span>
                      </div>
                    ) : (
                      "เข้าสู่ระบบ"
                    )}
                  </button>
                </div>
              </form>

              {/* Description */}
              {/* <div className="mt-4">
                <p className="small">
                  <span className="pointer" onClick={() => setVisible(true)}>
                    {infoIcon}
                  </span>
                  หมายเหตุ : หมายเลขผู้ใช้น้ำ, หมายเลขเขต, รหัสมาตรวัดน้ำ
                  สามารถดูได้จากใบเสร็จที่ออกโดยสำนักการประปาเทศบาลนครนครราชสีมา
                  หรือติดต่อสอบถามข้อมูลได้ที่ สำนักการประปาเทศบาลนครนครราชสีมา
                  โทร 044-234656
                </p>
              </div> */}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      <Dialog
        header={
          <div className="d-flex justify-content-start align-items-center border-b p-2">
            ตัวอย่างใบเสร็จ
          </div>
        }
        visible={visible}
        className="my-font"
        style={isMobile ? { width: "90vw" } : { width: "50vw" }}
        onHide={() => setVisible(false)}
      >
        <BillExampleModal />
      </Dialog>
    </>
  );
};

const HomePage = () => {
  return <HomeForm />;
};

export default HomePage;
